/**
 * MonteCarloTab — Monte Carlo simulation results for the Analysis stage
 *
 * Phase 4.4 — redesign-p4-analatt-44b
 * Displays existing Monte Carlo engine outputs (ranges, percentiles, confidence bands)
 * Uses FinancialValue for money values — negative in muted-gray parens per design spec
 * No engine changes — UI wiring only
 */

import React, { useEffect, useState } from 'react';
import { BarChart3, RefreshCw, AlertCircle, TrendingUp, TrendingDown, Activity } from 'lucide-react';
import { FinancialValue } from '@/components/shared/FinancialValue';
import { formatCurrency } from '@/utils/financialFormat';
import { MetricCard } from '@/components/shared/MetricCard';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { runMonteCarlo, MonteCarloResult } from '@/services/analysisApi';
import { BRAND_NAME } from '@/config/brand';

interface MonteCarloTabProps {
  reportId: string;
}

export function MonteCarloTab({ reportId }: MonteCarloTabProps) {
  const [result, setResult] = useState<MonteCarloResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [iterations, setIterations] = useState(2000);
  const [noisePct, setNoisePct] = useState(15);

  const fetchMonteCarlo = async (opts?: { iterations?: number; noisePct?: number }) => {
    setLoading(true);
    setError(null);
    try {
      const data = await runMonteCarlo(reportId, {
        iterations: opts?.iterations ?? iterations,
        noisePct: (opts?.noisePct ?? noisePct) / 100,
      });
      setResult(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to run Monte Carlo simulation');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMonteCarlo();
  }, [reportId]);

  const handleRun = () => {
    fetchMonteCarlo({ iterations, noisePct });
  };

  if (loading && !result) {
    return (
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6">
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-[var(--color-brand)]" />
          <span className="ml-3 text-sm text-[var(--color-text-secondary)]">
            Running Monte Carlo simulation…
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6">
        <div className="rounded-lg bg-[rgba(255,102,0,0.08)] p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-[var(--color-brand)]" />
            <p className="text-sm text-[var(--color-text-primary)]">{error}</p>
          </div>
          <button
            onClick={handleRun}
            className="mt-3 rounded-md bg-[var(--color-brand)] px-4 py-2 text-sm font-medium text-[var(--color-on-brand)] hover:bg-[var(--color-brand-hover)]"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!result) return null;

  const { base_claim, iterations: runIterations, noise_pct, results, distribution_sample } = result;
  const { mean, median, std_dev, ci_95_low, ci_95_high, min, max } = results;

  // Percentiles come from the deterministic engine (np.percentile over the full
  // simulated distribution). We RENDER them — never interpolate fake p10/p90 from
  // min/max. Older cached runs without these fields render "—" (not fabricated).
  const p10 = typeof (results as any).p10 === 'number' ? (results as any).p10 as number : null;
  const p50 = median;
  const p90 = typeof (results as any).p90 === 'number' ? (results as any).p90 as number : null;

  // Create histogram bins from distribution sample
  const histogramBins = 20;
  const binWidth = (max - min) / histogramBins || 1;
  const bins = Array.from({ length: histogramBins }, (_, i) => {
    const binMin = min + i * binWidth;
    const binMax = binMin + binWidth;
    const count = distribution_sample?.filter((v) => v >= binMin && v < binMax).length || 0;
    return { min: binMin, max: binMax, count };
  });
  const maxBinCount = Math.max(...bins.map((b) => b.count), 1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-[var(--color-needs-human-tint)] p-2">
            <BarChart3 className="h-5 w-5 text-[var(--color-brand)]" />
          </div>
          <div>
            <h2
              className="text-lg font-semibold text-[var(--color-text-primary)]"
              style={{ fontFamily: 'DM Serif Display, Georgia, serif' }}
            >
              Monte Carlo Simulation
            </h2>
            <p className="text-xs text-[var(--color-text-muted)]">
              {runIterations.toLocaleString()} iterations • ±{(noise_pct * 100).toFixed(0)}% noise
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs text-[var(--color-text-muted)]">Iterations:</label>
            <select
              value={iterations}
              onChange={(e) => setIterations(Number(e.target.value))}
              className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-2 py-1 text-xs text-[var(--color-text-secondary)]"
            >
              <option value={1000}>1,000</option>
              <option value={2000}>2,000</option>
              <option value={5000}>5,000</option>
              <option value={10000}>10,000</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-[var(--color-text-muted)]">Noise:</label>
            <select
              value={noisePct}
              onChange={(e) => setNoisePct(Number(e.target.value))}
              className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-2 py-1 text-xs text-[var(--color-text-secondary)]"
            >
              <option value={10}>10%</option>
              <option value={15}>15%</option>
              <option value={20}>20%</option>
              <option value={25}>25%</option>
            </select>
          </div>
          <button
            onClick={handleRun}
            disabled={loading}
            className="flex items-center gap-2 rounded-md bg-[var(--color-brand)] px-3 py-1.5 text-sm font-medium text-[var(--color-on-brand)] hover:bg-[var(--color-brand-hover)] disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Run
          </button>
        </div>
      </div>

      {/* Percentile Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <MetricCard
          label="P10 (Conservative)"
          value={<FinancialValue value={p10} size="lg" />}
          subtitle="10% chance below this"
        />
        <MetricCard
          label="P50 (Median)"
          value={<FinancialValue value={p50} size="lg" />}
          subtitle="50th percentile — most likely"
          accent
        />
        <MetricCard
          label="P90 (Aggressive)"
          value={<FinancialValue value={p90} size="lg" />}
          subtitle="90% chance below this"
          badge={{ text: 'Upper Bound', variant: 'active' }}
        />
      </div>

      {/* Statistical Summary */}
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-5">
        <h3
          className="mb-4 text-base font-semibold text-[var(--color-text-primary)]"
          style={{ fontFamily: 'DM Serif Display, Georgia, serif' }}
        >
          Statistical Summary
        </h3>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div className="rounded-lg bg-[var(--color-bg-elevated)] p-3">
            <p className="text-xs text-[var(--color-text-muted)]">Base Claim</p>
            <FinancialValue value={base_claim} size="lg" />
          </div>
          <div className="rounded-lg bg-[var(--color-bg-elevated)] p-3">
            <p className="text-xs text-[var(--color-text-muted)]">Mean</p>
            <FinancialValue value={mean} size="lg" />
          </div>
          <div className="rounded-lg bg-[var(--color-bg-elevated)] p-3">
            <p className="text-xs text-[var(--color-text-muted)]">Std Deviation</p>
            <FinancialValue value={std_dev} size="lg" />
          </div>
          <div className="rounded-lg bg-[var(--color-bg-elevated)] p-3">
            <p className="text-xs text-[var(--color-text-muted)]">Median</p>
            <FinancialValue value={median} size="lg" />
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-4">
          <div className="rounded-lg border border-[var(--color-needs-human-tint-bd)] bg-[var(--color-needs-human-tint)] p-3">
            <div className="flex items-center gap-2">
              <TrendingDown className="h-4 w-4 text-[var(--color-brand)]" />
              <p className="text-xs text-[var(--color-text-muted)]">95% CI Lower</p>
            </div>
            <FinancialValue value={ci_95_low} size="lg" />
          </div>
          <div className="rounded-lg border border-[var(--color-verified)] border-opacity-30 bg-transparent p-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-[var(--color-verified)]" />
              <p className="text-xs text-[var(--color-text-muted)]">95% CI Upper</p>
            </div>
            <FinancialValue value={ci_95_high} size="lg" />
          </div>
        </div>
      </div>

      {/* Distribution Histogram */}
      {distribution_sample && distribution_sample.length > 0 && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-5">
          <h3
            className="mb-4 text-base font-semibold text-[var(--color-text-primary)]"
            style={{ fontFamily: 'DM Serif Display, Georgia, serif' }}
          >
            Distribution Histogram
          </h3>
          <div className="relative h-40">
            <div className="flex h-full items-end gap-1">
              {bins.map((bin, idx) => {
                const height = (bin.count / maxBinCount) * 100;
                const isMedianBin = bin.min <= median && median < bin.max;
                return (
                  <div
                    key={idx}
                    className="flex-1 transition-all hover:opacity-80"
                    style={{ height: `${Math.max(height, 2)}%` }}
                  >
                    <div
                      className={`h-full w-full rounded-t ${
                        isMedianBin
                          ? 'bg-[var(--color-brand)]'
                          : 'bg-[var(--color-brand)] opacity-40'
                      }`}
                      title={`${formatCurrency(bin.min)} - ${formatCurrency(bin.max)}: ${bin.count} samples`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
          <div className="mt-2 flex justify-between text-xs text-[var(--color-text-muted)]">
            <span>Min: <FinancialValue value={min} /></span>
            <span className="text-[var(--color-brand)]">Median: <FinancialValue value={median} /></span>
            <span>Max: <FinancialValue value={max} /></span>
          </div>
        </div>
      )}

      {/* Interpretation */}
      <div className="rounded-xl border border-[var(--color-needs-human-tint-bd)] bg-[var(--color-needs-human-tint)] p-5">
        <div className="flex items-start gap-3">
          <Activity className="mt-0.5 h-5 w-5 flex-shrink-0 text-[var(--color-brand)]" />
          <div>
            <h4 className="mb-1 font-medium text-[var(--color-brand)]">Interpretation for Settlement</h4>
            <p className="text-sm text-[var(--color-text-secondary)]">
              Based on {runIterations.toLocaleString()} simulations with ±{(noise_pct * 100).toFixed(0)}% variability,
              there is a <strong>90% probability</strong> that the actual loss will be below{' '}
              <FinancialValue value={p90} />. The P90 figure provides a defensible upper bound for
              opening demands while maintaining actuarial credibility. The 95% confidence interval
              ranges from <FinancialValue value={ci_95_low} /> to <FinancialValue value={ci_95_high} />.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MonteCarloTab;
